import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { useSection, useSiteContent } from "@/contexts/SiteContentContext";
import TrendingOnCBR from "@/components/TrendingOnCBR";
import * as Lucide from "lucide-react";
import { ArrowRight, Activity, BarChart3, Target, ShieldCheck, Trophy, Users, UserPlus, ChevronLeft, ChevronRight, Star, GraduationCap, Zap, Award, Calendar, MapPin, Clock, TrendingUp } from "lucide-react";

const PillarIcon = ({ name, className }) => {
  const C = Lucide[name] || Star;
  return <C className={className} strokeWidth={1.5} />;
};

export default function Home() {
  const [camps, setCamps] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [topProspects, setTopProspects] = useState([]);
  const [feed, setFeed] = useState([]);
  const [events, setEvents] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [banners, setBanners] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [sections, setSections] = useState(null);
  const heroContent = useSection("home_hero");
  const pillarsIntro = useSection("home_pillars_intro");
  const ctaContent = useSection("home_cta");
  const { pillars } = useSiteContent();

  useEffect(() => {
    api.get("/camps").then(({ data }) => setCamps((data.items || []).slice(0, 3))).catch(() => {});
    api.get("/players/featured", { params: { limit: 5 } }).then(({ data }) => setFeatured(data.items || [])).catch(() => {});
    api.get("/rankings", { params: { limit: 5 } }).then(({ data }) => setTopProspects(data.rankings || [])).catch(() => {});
    api.get("/events/upcoming", { params: { limit: 5 } }).then(({ data }) => setEvents(data.items || [])).catch(() => {});
    api.get("/commitments").then(({ data }) => setCommitments((data.items || []).slice(0, 5))).catch(() => {});
    api.get("/hero-banners").then(({ data }) => setBanners(data.items || [])).catch(() => {});
    api.get("/articles", { params: { limit: 5 } }).then(({ data }) => setLatestArticles(data.items || [])).catch(() => {});
    api.get("/homepage-config").then(({ data }) => setSections(data.sections || [])).catch(() => setSections([]));
  }, []);

  useEffect(() => {
    const loadFeed = () => api.get("/activity/feed", { params: { limit: 5 } }).then(({ data }) => setFeed(data.items || [])).catch(() => {});
    loadFeed();
    const t = setInterval(loadFeed, 30000);
    return () => clearInterval(t);
  }, []);

  const isVisible = (key) => {
    if (!sections) return key === "hero";
    const s = sections.find((x) => x.key === key);
    return s ? s.visible : false;
  };

  return (
    <PublicLayout>
      {/* Hero (banner-managed with static fallback) */}
      <HeroSection banners={banners} camps={camps} visible={isVisible("hero")} content={heroContent} />

      {/* Mobile-only CTAs (desktop shows these inside the hero) */}
      <section className="bg-[#0A192F] lg:hidden px-6 pb-10 -mt-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/trips" data-testid="hero-cta-trips-mobile" className="bg-[#E63946] p-5 rounded-sm flex flex-col">
            <Trophy className="w-6 h-6 text-white/90" />
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85 mt-3">CBR Nation Trips</div>
            <div className="font-display text-xl uppercase tracking-tight text-white mt-1 leading-tight">Travel &amp; Compete with CBR Nation</div>
            <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-white">See Upcoming Events <ArrowRight className="w-3.5 h-3.5" /></span>
          </Link>
          <Link to="/create-profile" data-testid="hero-cta-free-profile-mobile" className="bg-white/[0.06] border border-white/15 p-5 rounded-sm flex flex-col">
            <UserPlus className="w-6 h-6 text-[#E63946]" />
            <div className="label-overline text-white/50 mt-3">Free Profile</div>
            <div className="font-display text-xl uppercase tracking-tight text-white mt-1 leading-tight">Create Your Free CBR Profile</div>
            <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-white">Get Started <ArrowRight className="w-3.5 h-3.5" /></span>
          </Link>
        </div>
      </section>

      {/* Trending on CBR — live ecosystem activity feed */}
      <TrendingOnCBR />

      {/* Features bento (brand) */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="label-overline text-[#E63946]">{pillarsIntro.overline || "The Platform"}</div>
              <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-3 max-w-3xl">
                {pillarsIntro.title || "Six pillars of serious player evaluation"}
              </h2>
              {pillarsIntro.subtitle && <p className="text-base text-slate-600 mt-4 max-w-2xl">{pillarsIntro.subtitle}</p>}
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {(pillars && pillars.length ? pillars : []).map((f) => (
              <div key={f.id || f.title} data-testid={`pillar-card-${f.id || ""}`} className="bg-white p-8 hover:bg-[#F8F9FA] transition-colors">
                <PillarIcon name={f.icon} className="w-7 h-7 text-[#E63946]" />
                <h3 className="font-display text-xl uppercase tracking-tight mt-5">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Managed homepage sections (order/visibility/title controlled by admin) */}
      {(sections || []).filter((s) => s.visible && s.key !== "hero").map((s) => (
        <React.Fragment key={s.key}>{renderSection(s.key, s.title, { featured, feed, events, commitments, topProspects, latestArticles })}</React.Fragment>
      ))}

      {/* CTA strip */}
      <section className="bg-[#0A192F] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-overline text-[#E63946]">{ctaContent.overline || "Recruiting · Rankings · Recognition"}</div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-4">
              {ctaContent.title || "Build your permanent recruiting profile."}
            </h2>
            <p className="text-white/70 mt-6 max-w-xl">
              {ctaContent.body || "Create a free account, register your player for a CBR event, and start building a verified record that scouts and college coaches actually trust."}
            </p>
          </div>
          <div className="flex flex-wrap gap-4 lg:justify-end">
            <Link
              to={ctaContent.cta_url || "/signup"}
              data-testid="footer-cta-signup"
              className="bg-[#E63946] hover:bg-[#D90429] px-8 py-4 text-sm font-semibold uppercase tracking-wider rounded-sm"
            >
              {ctaContent.cta_text || "Join CBR"}
            </Link>
            <Link
              to={ctaContent.secondary_url || "/camps"}
              data-testid="footer-cta-camps"
              className="border border-white/30 hover:bg-white hover:text-[#0A192F] px-8 py-4 text-sm font-semibold uppercase tracking-wider rounded-sm"
            >
              {ctaContent.secondary_text || "See Camps"}
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

const HERO_TILES = [
  { id: "shop", to: "/marketplace", icon: Lucide.ShoppingBag, label: "Shop CBR Gear", pitch: "Official CBR apparel & gear" },
  { id: "membership", to: "/membership", icon: Lucide.Lock, label: "Unlock Full Rankings", pitch: "Members see every ranking & leaderboard" },
  { id: "evaluated", to: "/camps", icon: ShieldCheck, label: "Get Evaluated", pitch: "Earn CBR-verified metrics at a camp" },
  { id: "sponsor", to: "/sponsorship", icon: Lucide.Handshake, label: "Sponsor CBR", pitch: "Put your brand in front of Canada's top prospects" },
];

function HeroSection({ banners, camps, visible, content = {} }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!banners || banners.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 7000);
    return () => clearInterval(t);
  }, [banners]);
  if (visible === false) return null;

  const b = (banners && banners.length > 0) ? banners[idx % banners.length] : null;
  const bgImage = b?.background_image_url || "https://customer-assets-jt897jd0.emergentagent.net/job_4834cf5d-eed7-460b-b9dd-2c7ded914952/artifacts/94852a498ff4e692_Screenshot_20261010_162636_Instagram.webp";
  const headline = b?.title || content.title || "Where Canada's next ballplayers get seen";
  const subtitle = b?.subtitle || content.subtitle || "CBR Prospects identifies, measures, evaluates and ranks Canadian amateur players — then puts them in front of the scouts and programs that matter.";
  const ctaText = b?.cta_text || content.cta_text || "Browse Upcoming Camps";
  const ctaLink = b?.cta_link || content.cta_url || "/camps";
  const overline = content.overline || "Canadian Baseball · CBR Nation";

  return (
    <section data-testid="hero-section" className="relative bg-[#0A192F] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-70" style={{ backgroundImage: `url('${bgImage}')`, backgroundSize: "cover", backgroundPosition: "center 25%" }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/85 to-[#0A192F]/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-transparent to-[#0A192F]/40" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-32 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-8">
            <span className="block w-2 h-2 bg-[#E63946] animate-pulse" />
            <span className="label-overline text-white/70">{overline}</span>
          </div>
          <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.95]">{headline}</h1>
          <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">{subtitle}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={ctaLink} data-testid="hero-cta-camps" className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#D90429] px-7 py-4 text-sm font-semibold tracking-wider uppercase rounded-sm transition-colors">
              {ctaText} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/signup" data-testid="hero-cta-join" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white hover:text-[#0A192F] px-7 py-4 text-sm font-semibold tracking-wider uppercase rounded-sm transition-colors">Join CBR</Link>
          </div>
          {banners && banners.length > 1 && (
            <div className="mt-8 flex items-center gap-1.5">
              {banners.map((_, i) => (
                <button key={i} data-testid={`hero-dot-${i}`} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#E63946]" : "w-2 bg-white/30"}`} />
              ))}
            </div>
          )}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl border-t border-white/10 pt-8" data-testid="hero-revenue-tiles">
            {HERO_TILES.map((t) => (
              <Link key={t.to} to={t.to} data-testid={`hero-tile-${t.id}`}
                className="group bg-white/[0.04] hover:bg-white/[0.09] border border-white/10 hover:border-[#E63946] p-4 rounded-sm transition-colors flex flex-col">
                <t.icon className="w-5 h-5 text-[#E63946]" />
                <div className="font-display text-lg uppercase tracking-tight text-white mt-3 leading-tight">{t.label}</div>
                <div className="text-xs text-white/60 mt-1 leading-snug">{t.pitch}</div>
                <ArrowRight className="w-4 h-4 text-white/70 mt-auto pt-3 box-content group-hover:translate-x-1 group-hover:text-[#E63946] transition-transform" />
              </Link>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 hidden lg:flex flex-col gap-4 self-end">
          <div className="bg-white/[0.03] backdrop-blur border border-white/10 p-6 rounded-sm">
            <div className="label-overline text-white/50">Next CBR Event</div>
            {camps[0] ? (
              <>
                <div className="font-display text-2xl uppercase tracking-tight mt-2">{camps[0].title}</div>
                <div className="text-sm text-white/60 mt-2">{camps[0].city}, {camps[0].province} · {new Date(camps[0].start_date).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}</div>
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/10">
                  <div><div className="label-overline text-white/50">From</div><div className="font-data text-3xl mt-1">${camps[0].price}</div></div>
                  <Link to={`/camps/${camps[0].slug}`} className="bg-[#E63946] px-5 py-3 text-xs font-semibold uppercase tracking-wider rounded-sm">Register</Link>
                </div>
              </>
            ) : <div className="font-display text-2xl mt-2 uppercase">Coming soon</div>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Link to="/trips" data-testid="hero-cta-trips" className="group bg-[#E63946] hover:bg-[#D90429] p-5 rounded-sm transition-colors flex flex-col min-h-[190px]">
              <Trophy className="w-6 h-6 text-white/90" />
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85 mt-3">CBR Nation Trips</div>
              <div className="font-display text-xl uppercase tracking-tight text-white mt-1 leading-tight">Travel &amp; Compete with CBR Nation</div>
              <span className="mt-auto pt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-white">See Upcoming Events <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
            </Link>
            <Link to="/create-profile" data-testid="hero-cta-free-profile" className="group bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur border border-white/15 p-5 rounded-sm transition-colors flex flex-col min-h-[190px]">
              <UserPlus className="w-6 h-6 text-[#E63946]" />
              <div className="label-overline text-white/50 mt-3">Free Profile</div>
              <div className="font-display text-xl uppercase tracking-tight text-white mt-1 leading-tight">Create Your Free CBR Profile</div>
              <span className="mt-auto pt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-white">Get Started <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedProspects({ players, title }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [players]);
  useEffect(() => {
    if (players.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % players.length), 6000);
    return () => clearInterval(t);
  }, [players]);

  if (!players || players.length === 0) return null;
  const p = players[idx];

  return (
    <section data-testid="featured-prospects" className="bg-white py-24 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10 gap-4">
          <div>
            <div className="label-overline text-[#E63946]">Spotlight</div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-3">{title || "Featured Prospects"}</h2>
          </div>
          <Link to="/prospects" className="text-sm font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946] inline-flex items-center gap-2">
            All Prospects <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid lg:grid-cols-12 gap-0 border border-slate-200 rounded-sm overflow-hidden">
          <div className="lg:col-span-5 bg-[#0A192F] relative min-h-[280px]">
            {p.profile_photo_url ? (
              <img src={p.profile_photo_url} alt={`${p.first_name} ${p.last_name}`} className="absolute inset-0 w-full h-full object-cover opacity-90" />
            ) : (
              <div className="absolute inset-0 grid place-items-center"><span className="font-display text-7xl text-white/20">{p.first_name?.[0]}{p.last_name?.[0]}</span></div>
            )}
            <div className="absolute top-4 left-4"><span className="inline-flex items-center gap-1 bg-[#E63946] text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1"><Star className="w-3 h-3" /> Featured</span></div>
          </div>
          <div className="lg:col-span-7 p-8 flex flex-col justify-center">
            <div className="label-overline text-slate-400">Class of {p.graduation_year || "—"} · {p.primary_position || "—"} · {p.province || "—"}</div>
            <h3 data-testid="featured-player-name" className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-2">{p.first_name} <span className="text-[#E63946]">{p.last_name}</span></h3>
            <div className="mt-6 flex flex-wrap gap-8">
              <div>
                <div className="label-overline text-slate-400">CBR Overall Rating</div>
                <div className="font-data text-5xl text-[#0A192F] mt-1">{p.cbr_overall_rating != null ? Number(p.cbr_overall_rating).toFixed(1) : "—"}</div>
              </div>
              {p.key_metric && (
                <div>
                  <div className="label-overline text-slate-400">{p.key_metric.label}</div>
                  <div className="font-data text-5xl text-[#0A192F] mt-1">{p.key_metric.value}<span className="text-lg text-slate-400"> {p.key_metric.units}</span></div>
                </div>
              )}
            </div>
            <div className="mt-8 flex items-center gap-4">
              <Link to={`/prospects/${p.slug}`} data-testid="featured-view-profile" className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#1a2a4a] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
                View Full Profile <ArrowRight className="w-4 h-4" />
              </Link>
              {players.length > 1 && (
                <div className="flex items-center gap-2 ml-auto">
                  <button data-testid="featured-prev" onClick={() => setIdx((i) => (i - 1 + players.length) % players.length)} className="p-2 border border-slate-300 rounded-sm hover:border-[#E63946] hover:text-[#E63946]"><ChevronLeft className="w-4 h-4" /></button>
                  <div className="flex items-center gap-1.5">
                    {players.map((_, i) => (
                      <button key={i} data-testid={`featured-dot-${i}`} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? "bg-[#E63946]" : "bg-slate-300 hover:bg-slate-400"}`} />
                    ))}
                  </div>
                  <button data-testid="featured-next" onClick={() => setIdx((i) => (i + 1) % players.length)} className="p-2 border border-slate-300 rounded-sm hover:border-[#E63946] hover:text-[#E63946]"><ChevronRight className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const ACTIVITY_META = {
  commitment: { icon: GraduationCap, color: "text-emerald-600 bg-emerald-50" },
  verified_metric: { icon: Zap, color: "text-[#E63946] bg-[#E63946]/10" },
  camp_award: { icon: Award, color: "text-amber-600 bg-amber-50" },
  ranking_change: { icon: TrendingUp, color: "text-indigo-600 bg-indigo-50" },
  cbr_nation_invite: { icon: ShieldCheck, color: "text-sky-700 bg-sky-50" },
  featured: { icon: Star, color: "text-purple-600 bg-purple-50" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

const fmtEventDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "TBD");

function RecentActivitySection({ feed, title }) {
  return (
    <section data-testid="recent-activity-feed" className="bg-white py-20 border-t border-slate-200">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="label-overline text-[#E63946]">Live</div>
            <h2 className="font-display text-3xl lg:text-4xl uppercase tracking-tighter mt-3">{title || "Recent Activity"}</h2>
          </div>
          <Link to="/trending" className="text-sm font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946] inline-flex items-center gap-2">View All <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {feed.length === 0 ? (
          <p className="text-sm text-slate-400">No recent activity yet — check back once games and commitments start rolling in.</p>
        ) : (
          <div className="space-y-1">
            {feed.map((a, i) => {
              const m = ACTIVITY_META[a.type] || ACTIVITY_META.featured;
              const Inner = (
                <div className="flex items-center gap-3 p-3 rounded-sm hover:bg-slate-50 transition-colors">
                  <div className="w-10 h-10 rounded-sm bg-slate-100 overflow-hidden shrink-0">
                    {a.player_photo_url
                      ? <img src={a.player_photo_url} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full grid place-items-center font-display text-slate-300">{(a.player_name || "?")[0]}</div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm"><span className="font-semibold">{a.player_name}</span> <span className="text-slate-500">{a.text}</span></div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" />{timeAgo(a.created_at)}</div>
                  </div>
                  <span className={`w-7 h-7 rounded-sm grid place-items-center shrink-0 ${m.color}`}><m.icon className="w-4 h-4" /></span>
                </div>
              );
              return a.link
                ? <Link key={i} to={a.link} data-testid={`activity-item-${i}`} className="block">{Inner}</Link>
                : <div key={i} data-testid={`activity-item-${i}`}>{Inner}</div>;
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function UpcomingEventsSection({ events, title }) {
  return (
    <section data-testid="upcoming-events" className="bg-[#F8F9FA] py-20 border-t border-slate-200">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="label-overline text-[#E63946]">Calendar</div>
            <h2 className="font-display text-3xl lg:text-4xl uppercase tracking-tighter mt-3">{title || "Upcoming Events"}</h2>
          </div>
          <Link to="/camps" className="text-sm font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946] inline-flex items-center gap-2">All Camps <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {events.length === 0 ? (
          <p className="text-sm text-slate-400">No upcoming events scheduled.</p>
        ) : (
          <div className="space-y-3">
            {events.map((e, i) => (
              <div key={e.id || i} data-testid={`event-item-${i}`} className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-4">
                <div className="text-center shrink-0 w-14">
                  <div className="font-display text-2xl leading-none text-[#0A192F]">{e.start_date ? new Date(e.start_date).getDate() : "–"}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{e.start_date ? new Date(e.start_date).toLocaleDateString("en-CA", { month: "short" }) : ""}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{e.event_type}</span>
                  <div className="font-semibold mt-1 truncate">{e.title}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{e.location || "TBD"}{e.registration_deadline && <span className="ml-2">· Reg. by {fmtEventDate(e.registration_deadline)}</span>}</div>
                </div>
                {e.register_link && (
                  <Link to={e.register_link} data-testid={`event-register-${i}`} className="shrink-0 bg-[#E63946] hover:bg-[#D90429] text-white px-4 py-2 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition-colors">Register Now</Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function LatestArticlesSection({ articles, title }) {
  if (!articles || articles.length === 0) return null;
  return (
    <section data-testid="latest-articles" className="bg-white py-20 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="label-overline text-[#E63946]">The Wire</div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-3">{title || "Latest Articles"}</h2>
          </div>
          <Link to="/news" className="text-sm font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946] inline-flex items-center gap-2">All News <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.slice(0, 5).map((a) => (
            <Link key={a.id} to={`/news/${a.slug}`} data-testid={`home-article-${a.id}`} className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[#0A192F] transition-colors">
              <div className="aspect-[16/9] bg-gradient-to-br from-[#0A192F] to-[#1a2a4a] relative grid place-items-center">
                {a.cover_image_url ? <img src={a.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <span className="text-[9px] uppercase tracking-widest text-white/30">CBR Prospects</span>}
              </div>
              <div className="p-4">
                <span className="label-overline text-[#E63946] text-[10px]">{(a.category || "").replace(/_/g, " ")}</span>
                <h3 className="font-semibold mt-1 group-hover:text-[#E63946] line-clamp-2">{a.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function RecognitionSection({ title }) {
  const [awards, setAwards] = useState([]);
  useEffect(() => { api.get("/awards/stats").then(({ data }) => setAwards((data.most_awards || []).slice(0, 5))).catch(() => {}); }, []);
  if (awards.length === 0) return null;
  return (
    <section data-testid="recognition-section" className="bg-[#0A192F] text-white py-20 border-t-4 border-[#E63946]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="label-overline text-[#E63946]">Recognition</div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-3">{title || "Recognition"}</h2>
          </div>
          <Link to="/awards" className="text-sm font-semibold uppercase tracking-wider text-white/70 hover:text-white inline-flex items-center gap-2">All Awards <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {awards.map((r) => (
            <Link key={r.player_id} to={`/prospects/${r.player_slug}`} className="bg-white/[0.04] border border-white/10 rounded-sm p-4 flex items-center gap-3 hover:bg-white/[0.08] transition-colors">
              <span className="w-9 h-9 rounded-sm bg-[#E63946]/20 text-[#E63946] grid place-items-center shrink-0"><Award className="w-4 h-4" /></span>
              <div className="min-w-0 flex-1"><div className="font-semibold truncate">{r.player_name}</div><div className="text-[11px] text-white/50">{[r.player_position, r.player_grad_year].filter(Boolean).join(" · ")}</div></div>
              <span className="font-data text-2xl text-white">{r.total}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function MediaWallSection({ title }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/media-wall", { params: { limit: 8 } }).then(({ data }) => setItems(data.items || [])).catch(() => setItems([])); }, []);
  if (items.length === 0) return null;
  return (
    <section data-testid="media-wall-section" className="bg-[#0A192F] text-white py-20 border-t-4 border-[#E63946]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="label-overline text-[#E63946]">Gallery</div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-3">{title || "CBR Media Wall"}</h2>
          </div>
          <Link to="/media-wall" className="text-sm font-semibold uppercase tracking-wider text-white/70 hover:text-white inline-flex items-center gap-2">View Media Wall <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.slice(0, 8).map((it) => {
            const Card = (
              <div className="rounded-sm overflow-hidden border border-white/10 bg-white/[0.03] hover:border-[#E63946] transition-colors">
                <div className="aspect-square"><img src={it.card_image_url} alt={it.player_name || ""} className="w-full h-full object-cover" loading="lazy" /></div>
              </div>
            );
            return it.link
              ? <Link key={it.id} to={it.link} data-testid={`home-media-${it.id}`}>{Card}</Link>
              : <div key={it.id} data-testid={`home-media-${it.id}`}>{Card}</div>;
          })}
        </div>
      </div>
    </section>
  );
}

function SponsorsSection({ title }) {
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => { api.get("/sponsors").then(({ data }) => setSponsors(data.items || [])).catch(() => setSponsors([])); }, []);
  if (sponsors.length === 0) return null;
  return (
    <section data-testid="sponsors-section" className="bg-white py-16 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <div className="label-overline text-slate-400">{title || "Our Partners"}</div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-8">
          {sponsors.map((s) => (
            <a key={s.id} href={s.website || "#"} target="_blank" rel="noreferrer" data-testid={`sponsor-${s.id}`} className="opacity-70 hover:opacity-100 transition-opacity">
              {s.logo_url ? <img src={s.logo_url} alt={s.name} className="h-12 object-contain" /> : <span className="font-display uppercase text-slate-500">{s.name}</span>}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function renderSection(key, title, data) {
  switch (key) {
    case "featured_prospects": return <FeaturedProspects players={data.featured} title={title} />;
    case "recent_activity": return <RecentActivitySection feed={data.feed} title={title} />;
    case "upcoming_events": return <UpcomingEventsSection events={data.events} title={title} />;
    case "recent_commitments": return <RecentCommitments commitments={data.commitments} title={title} />;
    case "top_prospects": return <TopProspectsPreview players={data.topProspects} title={title} />;
    case "latest_articles": return <LatestArticlesSection articles={data.latestArticles} title={title} />;
    case "recognition": return <RecognitionSection title={title} />;
    case "media_wall": return <MediaWallSection title={title} />;
    case "sponsors": return <SponsorsSection title={title} />;
    default: return null;
  }
}

const LEVEL_BADGE = {
  "NCAA D1": "bg-[#E63946] text-white", "NCAA D2": "bg-[#0A192F] text-white",
  "NCAA D3": "bg-indigo-600 text-white", "NAIA": "bg-emerald-600 text-white",
  "JUCO": "bg-amber-600 text-white", "U Sports": "bg-sky-700 text-white", "Pro": "bg-purple-700 text-white",
};

function RecentCommitments({ commitments, title }) {
  if (!commitments || commitments.length === 0) return null;
  return (
    <section data-testid="recent-commitments" className="bg-[#F8F9FA] py-24 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between mb-10 gap-4">
          <div>
            <div className="label-overline text-[#E63946]">Recruiting</div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-3">{title || "Recent Commitments"}</h2>
          </div>
          <Link to="/commitments" className="text-sm font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946] inline-flex items-center gap-2">All Commitments <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {commitments.map((c) => (
            <Link key={c.id} to={`/prospects/${c.slug}`} data-testid={`recent-commitment-${c.id}`}
              className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-4 hover:border-[#0A192F] transition-colors group">
              <div className="w-16 h-16 rounded-sm bg-slate-100 overflow-hidden shrink-0">
                {c.profile_photo_url
                  ? <img src={c.profile_photo_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full grid place-items-center font-display text-2xl text-slate-300">{(c.first_name || "?")[0]}{(c.last_name || "")[0]}</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold group-hover:text-[#E63946] truncate">{c.first_name} {c.last_name}</div>
                <div className="text-[11px] text-slate-500">{[c.primary_position, c.graduation_year].filter(Boolean).join(" · ")}</div>
                <div className="mt-1.5 font-display uppercase tracking-tight text-sm text-[#0A192F] truncate">{c.school_name}</div>
                {c.level && <span className={`inline-block mt-1 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${LEVEL_BADGE[c.level] || "bg-slate-200"}`}>{c.level}</span>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function TopProspectsPreview({ players, title }) {
  if (!players || players.length === 0) return null;
  return (
    <section data-testid="top-prospects-preview" className="bg-[#F8F9FA] py-24 border-t border-slate-200">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <div className="label-overline text-[#E63946]">Rankings</div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-3">{title || "Top CBR Prospects"}</h2>
            <p className="text-sm text-slate-500 mt-2">Ranked by CBR Overall Rating — verified metrics blended with evaluator grades.</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {players.map((p) => (
            <Link key={p.id} to={`/prospects/${p.slug}`} data-testid={`top-prospect-row-${p.rank}`}
              className="flex items-center gap-4 p-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <span className={`font-data text-2xl w-8 text-center ${p.rank <= 3 ? "text-[#E63946]" : "text-slate-400"}`}>{p.rank}</span>
              <div className="w-11 h-11 rounded-sm bg-slate-100 overflow-hidden shrink-0">
                {p.profile_photo_url
                  ? <img src={p.profile_photo_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full grid place-items-center font-display text-slate-300">{(p.first_name || "?")[0]}</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{p.first_name} {p.last_name}</div>
                <div className="text-[11px] text-slate-500">{[p.primary_position, p.graduation_year, p.province].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-data text-2xl text-[#0A192F] leading-none">{p.cbr_overall_rating != null ? Number(p.cbr_overall_rating).toFixed(1) : "—"}</div>
                <div className="text-[9px] uppercase tracking-wider text-slate-400 mt-0.5">CBR Rating</div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link to="/rankings" data-testid="home-view-full-leaderboard" className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#1a2a4a] text-white px-7 py-3.5 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
            View Full Leaderboard <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
